import { getPlatformIcon } from '@/components/creator-discovery/utils';
import {
  Badge,
  Box,
  Flex,
  HStack,
  Link,
  SimpleGrid,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { MentionPlatform, type Mention, type MentionTimelinePoint } from '@fluenceur/types';
import { ChatBubbleEmpty, Eye, Group, Heart, ShareAndroid, Sparks } from 'iconoir-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import CreatorAvatar from '../creators/CreatorAvatar';
import { MentionMetricPill } from './MentionMetricPill';

const formatPercent = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '--';
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value)}%`;
};

const STAT_ICON_SIZE = 15;

/**
 * Latest engagement counters for the preview header, projected from the newest
 * timeline point so the counter set stays a single source of truth with it.
 */
export type MentionPreviewStats = Pick<
  MentionTimelinePoint,
  'likes' | 'comments' | 'shares' | 'views'
>;

interface CreatorSummaryProps {
  mention: Mention;
  stats?: MentionPreviewStats;
  isStatsLoading?: boolean;
}

export function CreatorSummary({ mention, stats, isStatsLoading = false }: CreatorSummaryProps) {
  const { t } = useTranslation();
  const { author } = mention;
  const mentionTypeLabel = mention.mentionType.includes('caption')
    ? t('mentions.origin.mention')
    : mention.mentionType.replace('_', ' ');
  const platformIcon = getPlatformIcon(mention.platform, 14);

  // TikTok surfaces views + shares on top of likes/comments; Instagram keeps the
  // likes/comments pair. Values come from the latest timeline point. Each counter
  // is stats-driven, so it carries the loading state while the timeline is in flight.
  const engagementCounters: StatPillData[] =
    mention.platform === MentionPlatform.TIKTOK
      ? [
          {
            key: 'views',
            icon: <Eye width={STAT_ICON_SIZE} height={STAT_ICON_SIZE} />,
            label: t('mentions.details.views'),
            value: stats?.views ?? null,
            loading: isStatsLoading,
          },
          {
            key: 'likes',
            icon: <Heart width={STAT_ICON_SIZE} height={STAT_ICON_SIZE} />,
            label: t('mentions.details.likes'),
            value: stats?.likes ?? null,
            loading: isStatsLoading,
          },
          {
            key: 'comments',
            icon: <ChatBubbleEmpty width={STAT_ICON_SIZE} height={STAT_ICON_SIZE} />,
            label: t('mentions.details.comments'),
            value: stats?.comments ?? null,
            loading: isStatsLoading,
          },
          {
            key: 'shares',
            icon: <ShareAndroid width={STAT_ICON_SIZE} height={STAT_ICON_SIZE} />,
            label: t('mentions.details.shares'),
            value: stats?.shares ?? null,
            loading: isStatsLoading,
          },
        ]
      : [
          {
            key: 'likes',
            icon: <Heart width={STAT_ICON_SIZE} height={STAT_ICON_SIZE} />,
            label: t('mentions.details.likes'),
            value: stats?.likes ?? null,
            loading: isStatsLoading,
          },
          {
            key: 'comments',
            icon: <ChatBubbleEmpty width={STAT_ICON_SIZE} height={STAT_ICON_SIZE} />,
            label: t('mentions.details.comments'),
            value: stats?.comments ?? null,
            loading: isStatsLoading,
          },
        ];

  // Creator-level figures lead (always available), then the post-level counters.
  const statPills: StatPillData[] = [
    {
      key: 'followers',
      icon: <Group width={STAT_ICON_SIZE} height={STAT_ICON_SIZE} />,
      label: t('mentions.details.followers'),
      value: author.followersCount,
    },
    {
      key: 'engagement',
      icon: <Sparks width={STAT_ICON_SIZE} height={STAT_ICON_SIZE} />,
      label: t('mentions.intelligence.engagementRate'),
      value: formatPercent(author.engagementRate),
    },
    ...engagementCounters,
  ];

  return (
    <Box py={5}>
      <Flex gap={3} align="flex-start">
        <CreatorAvatar
          size="lg"
          creatorId={author.username}
          creatorName={author.username}
          fallbackSrc={author.profilePictureUrl}
          colorPalette="primary"
        />
        <VStack minW={0} flex={1} align="stretch" gap={0.5}>
          <Flex gap={2} align="flex-start" justify="space-between">
            <HStack gap={2} align="center" flexWrap="wrap" minW={0}>
              <Link
                href={author.platformUrl}
                target="_blank"
                rel="noreferrer"
                color="primary.600"
                fontWeight="semibold"
                fontSize="md"
                lineHeight="short"
                truncate
                _hover={{ color: 'primary.700', textDecoration: 'underline' }}
              >
                @{author.username}
              </Link>
              {platformIcon && (
                <Badge colorPalette="gray" variant="subtle">
                  {platformIcon}
                </Badge>
              )}
              <Badge colorPalette="gray" variant="subtle" textTransform="none">
                {mentionTypeLabel}
              </Badge>
            </HStack>
          </Flex>
          {author.bio && (
            <Text fontSize="xs" color="gray.600" lineHeight="short" lineClamp={2}>
              {author.bio}
            </Text>
          )}
        </VStack>
      </Flex>

      <SimpleGrid columns={2} gap={2} mt={4} data-testid="mention-creator-stats">
        {statPills.map((pill) =>
          pill.loading ? (
            <Skeleton key={pill.key} rounded="md">
              <MentionMetricPill icon={pill.icon} label={pill.label} value={pill.value} />
            </Skeleton>
          ) : (
            <MentionMetricPill
              key={pill.key}
              icon={pill.icon}
              label={pill.label}
              value={pill.value}
            />
          )
        )}
      </SimpleGrid>
    </Box>
  );
}

interface StatPillData {
  key: string;
  icon: ReactNode;
  label: string;
  /** A pre-formatted string (percent) renders verbatim; a number formats compactly. */
  value: number | string | null;
  loading?: boolean;
}
